import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Link2, PhoneCall, Search, UserRound, X } from 'lucide-react';
import { api } from '../api';
import { telephonyApi } from '../telephony/telephonyApi';
import type { BrowserCall } from '../telephony/types';

type CompanyOption = { id: string; name: string; full_name?: string | null; is_active?: boolean };
type ContactOption = { id: string; company_id?: string | null; full_name?: string | null; position?: string | null; phone?: string | null; is_active?: boolean };

type Props = {
  call: BrowserCall;
  onClose: () => void;
  onLinked: () => void;
};

function callNumber(call: BrowserCall): string {
  return String(call.direction === 'INBOUND' ? call.from_number || call.to_number || '' : call.to_number || call.from_number || '');
}

export function CallClientLinkDialog({ call, onClose, onLinked }: Props) {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState(call.company_id || '');
  const [contactId, setContactId] = useState(call.contact_id || '');
  const [companySearch, setCompanySearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');

  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: () => api<CompanyOption[]>('/companies'),
    staleTime: 60_000,
  });
  const contacts = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api<ContactOption[]>('/contacts'),
    staleTime: 60_000,
  });

  const visibleCompanies = useMemo(() => {
    const needle = companySearch.trim().toLocaleLowerCase('ru-RU');
    return (companies.data || [])
      .filter(item => item.is_active !== false || item.id === companyId)
      .filter(item => !needle || [item.name, item.full_name].some(value => String(value || '').toLocaleLowerCase('ru-RU').includes(needle)))
      .slice(0, 100);
  }, [companies.data, companyId, companySearch]);

  const companyContacts = useMemo(() => {
    const needle = contactSearch.trim().toLocaleLowerCase('ru-RU');
    return (contacts.data || [])
      .filter(item => item.company_id === companyId)
      .filter(item => item.is_active !== false || item.id === contactId)
      .filter(item => !needle || [item.full_name, item.position, item.phone].some(value => String(value || '').toLocaleLowerCase('ru-RU').includes(needle)))
      .slice(0, 100);
  }, [contacts.data, companyId, contactId, contactSearch]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  const link = useMutation({
    mutationFn: () => telephonyApi.linkCallClient(call.id, companyId, contactId || undefined),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['telephony-calls'] }),
        queryClient.invalidateQueries({ queryKey: ['client-history'] }),
      ]);
      onLinked();
    },
  });

  function chooseCompany(nextCompanyId: string) {
    setCompanyId(nextCompanyId);
    setContactId('');
    setContactSearch('');
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!companyId || link.isPending) return;
    link.mutate();
  }

  const selectedCompany = (companies.data || []).find(item => item.id === companyId);
  const selectedContact = (contacts.data || []).find(item => item.id === contactId);

  return <div className="modal-backdrop call-client-link-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !link.isPending) onClose(); }}>
    <form className="call-client-link-dialog" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <header>
        <span><Link2 /></span>
        <div><small>ЖУРНАЛ ЗВОНКОВ</small><h2>{call.company_id || call.contact_id ? 'Изменить связь с клиентом' : 'Привязать звонок к клиенту'}</h2><p>Выберите только существующую компанию и, при необходимости, её контакт.</p></div>
        <button type="button" className="icon-button" disabled={link.isPending} onClick={onClose}><X /></button>
      </header>

      <div className="call-client-link-call">
        <PhoneCall />
        <span><small>{call.direction === 'INBOUND' ? 'Входящий звонок' : 'Исходящий звонок'}</small><b>{callNumber(call) || 'Номер не определён'}</b></span>
        <time>{call.started_at ? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(call.started_at)) : ''}</time>
      </div>

      <div className="call-client-link-grid">
        <section>
          <div className="call-client-link-section-head"><span><Building2 /></span><div><b>Компания *</b><small>Обязательная связь звонка</small></div></div>
          <label className="call-client-link-search"><Search /><input value={companySearch} onChange={event => setCompanySearch(event.target.value)} placeholder="Поиск компании" autoFocus /></label>
          <div className="call-client-link-options" role="listbox" aria-label="Компании">
            {companies.isLoading && <div className="call-client-link-state">Загружаем компании…</div>}
            {companies.isError && <div className="call-client-link-state error">Не удалось загрузить компании</div>}
            {!companies.isLoading && visibleCompanies.map(company => <button type="button" key={company.id} className={company.id === companyId ? 'active' : ''} onClick={() => chooseCompany(company.id)} role="option" aria-selected={company.id === companyId}><i>{company.name.slice(0, 2).toUpperCase()}</i><span><b>{company.name}</b><small>{company.full_name || 'Карточка компании'}</small></span></button>)}
            {!companies.isLoading && !visibleCompanies.length && <div className="call-client-link-state">Компании не найдены</div>}
          </div>
        </section>

        <section className={!companyId ? 'disabled' : ''}>
          <div className="call-client-link-section-head"><span><UserRound /></span><div><b>Контакт</b><small>Необязательно</small></div></div>
          <label className="call-client-link-search"><Search /><input value={contactSearch} onChange={event => setContactSearch(event.target.value)} placeholder={companyId ? 'Поиск контакта' : 'Сначала выберите компанию'} disabled={!companyId} /></label>
          <div className="call-client-link-options contacts" role="listbox" aria-label="Контакты компании">
            {companyId && <button type="button" className={!contactId ? 'active' : ''} onClick={() => setContactId('')} role="option" aria-selected={!contactId}><i>—</i><span><b>Без конкретного контакта</b><small>Привязать только компанию</small></span></button>}
            {contacts.isLoading && companyId && <div className="call-client-link-state">Загружаем контакты…</div>}
            {contacts.isError && companyId && <div className="call-client-link-state error">Не удалось загрузить контакты</div>}
            {companyContacts.map(contact => <button type="button" key={contact.id} className={contact.id === contactId ? 'active' : ''} onClick={() => setContactId(contact.id)} role="option" aria-selected={contact.id === contactId}><i>{String(contact.full_name || 'К').slice(0, 2).toUpperCase()}</i><span><b>{contact.full_name || 'Без имени'}</b><small>{contact.position || contact.phone || 'Контакт компании'}</small></span></button>)}
            {!companyId && <div className="call-client-link-state">Контакты появятся после выбора компании</div>}
          </div>
        </section>
      </div>

      {link.isError && <div className="form-error call-client-link-error">{link.error instanceof Error ? link.error.message : 'Не удалось привязать звонок'}</div>}

      <footer>
        <div>{selectedCompany && <><Building2 /><span><small>Будет сохранено</small><b>{selectedCompany.name}{selectedContact ? ` · ${selectedContact.full_name || 'Контакт'}` : ''}</b></span></>}</div>
        <button type="button" className="button ghost" disabled={link.isPending} onClick={onClose}>Отмена</button>
        <button type="submit" className="button primary" disabled={!companyId || link.isPending}><Link2 /> {link.isPending ? 'Привязываем…' : 'Сохранить связь'}</button>
      </footer>
    </form>
  </div>;
}
